import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { getLssDashboard, seedTemplates } from '@/services/api';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import {
  PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend, LineChart, Line
} from 'recharts';
import {
  Activity, TrendingUp, CheckCircle2, AlertTriangle, Clock,
  FolderKanban, ClipboardCheck, BarChart3, Truck, Package,
  ShieldAlert, ArrowRight, Plus, FileText, Users, Target, Info,
  ChevronsUpDown, Check, X,
} from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';

// Dashboard widgets (extracted into /dashboard/widgets/* for maintainability).
import {
  BRAND, DONUT_COLORS, PHASE_COLORS_ARR,
  HealthRow, ScoreRing, ModuleProgressBar, CustomTooltip,
  WorkforceHeadcountWidget, BriefingWidget, GoalsWidget, ForecastAccuracyWidget,
  AbsenteeismWidget,
} from './dashboard/widgets';

export const DashboardPage = () => {
  const { user, isAdmin, isGuest, isMasterAdmin } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedWorkers, setSelectedWorkers] = useState([]);
  const [pickingFrom, setPickingFrom] = useState('');
  const [pickingTo, setPickingTo] = useState('');
  const [bewerkerDropdownOpen, setBewerkerDropdownOpen] = useState(false);
  const loadDashboard = async (pFrom, pTo) => {
    try {
      const d = await getLssDashboard({ picking_from: pFrom || pickingFrom, picking_to: pTo || pickingTo });
      setData(d);
    } catch (error) {
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadDashboard(); }, []);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-[60vh]">
          <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: BRAND.cyan, borderTopColor: 'transparent' }} />
        </div>
      </DashboardLayout>
    );
  }

  const h = data?.health || {};
  const proj = data?.projects || {};
  const audits = data?.audits || {};
  const kpis = data?.kpis || {};
  const checklists = data?.checklists || {};
  const picking = data?.picking || {};
  const expedition = data?.expedition || {};
  const damage = data?.damage || {};
  const workload = data?.workload || [];

  // Chart data
  const donutData = [
    { name: 'Not Started', value: proj.not_started || 0 },
    { name: 'Complete', value: proj.completed || 0 },
    { name: 'In Progress', value: proj.in_progress || 0 },
  ].filter(d => d.value > 0);

  const phaseData = Object.entries(proj.phases || {}).map(([name, val]) => ({ name, value: val }));

  const kpiCatData = (kpis.by_category || []).map(c => ({
    name: c.name?.length > 12 ? c.name.slice(0, 12) + '...' : c.name,
    Value: c.value,
    Target: c.target,
  }));

  const auditTrend = audits.trend || [];
  const workloadData = selectedWorkers.length > 0
    ? workload.filter(w => selectedWorkers.includes(w.name))
    : workload.slice(0, 6);

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="dashboard-page">
        {isGuest && (
          <div className="flex items-center gap-3 px-4 py-3 rounded-lg" style={{ backgroundColor: BRAND.cyanLight, border: `1px solid ${BRAND.cyan}40` }}>
            <Info className="h-5 w-5 flex-shrink-0" style={{ color: BRAND.cyanDark }} />
            <div>
              <p className="text-sm font-medium" style={{ color: BRAND.cyanDark }}>Sample Dashboard</p>
              <p className="text-xs" style={{ color: BRAND.slate }}>Data shown is illustrative. Sign in to see your real metrics.</p>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Lean Six Sigma Dashboard</h1>
            <p className="text-muted-foreground font-body mt-1">
              Continuous improvement performance at a glance
            </p>
          </div>
          <div className="flex gap-2">
            {isAdmin && !isGuest && (
              <Button variant="outline" onClick={async () => { await seedTemplates(); toast.success('Templates seeded'); }} data-testid="seed-templates-btn">
                <FileText className="h-4 w-4 mr-2" /> Seed Templates
              </Button>
            )}
          </div>
        </div>

        {/* ============ ROW 1: Health + Tasks Donut + DMAIC Progress ============ */}
        <div className="grid lg:grid-cols-3 gap-4">
          {/* Health Panel */}
          <Card data-testid="health-panel">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Activity className="h-5 w-5" style={{ color: BRAND.cyan }} />
                Health
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="flex items-center gap-6 mb-4">
                <div className="relative">
                  <ScoreRing score={h.overall_score || 0} size={90} />
                </div>
                <div className="flex-1 space-y-1">
                  <p className="text-xs text-muted-foreground">Overall Score</p>
                  <p className="text-3xl font-bold">{h.overall_score || 0}<span className="text-base font-normal text-muted-foreground">%</span></p>
                </div>
              </div>
              <HealthRow label="Progress" value={`${h.progress_pct || 0}% complete`} color={h.progress_pct >= 50 ? 'text-emerald-600' : 'text-amber-600'} />
              <HealthRow label="Tasks" value={`${h.tasks_completed || 0} of ${h.tasks_total || 0} done`} />
              <HealthRow label="Overdue" value={`${h.tasks_overdue || 0} tasks overdue`} color={h.tasks_overdue > 0 ? 'text-rose-600' : 'text-emerald-600'} />
              <HealthRow label="Quality" value={`${h.quality_score || 0}% avg score`} color={h.quality_score >= 70 ? 'text-emerald-600' : 'text-amber-600'} />
            </CardContent>
          </Card>

          {/* Tasks / Projects Donut */}
          <Card data-testid="tasks-donut-panel">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <FolderKanban className="h-5 w-5" style={{ color: BRAND.cyan }} />
                  Projects
                </CardTitle>
                <Badge variant="outline" className="text-xs">{proj.total || 0} total</Badge>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="flex items-center gap-3 mb-3 flex-wrap">
                {[{ label: 'Not Started', color: '#94a3b8' }, { label: 'Complete', color: BRAND.emerald }, { label: 'In Progress', color: BRAND.cyan }].map(l => (
                  <span key={l.label} className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: l.color }} />{l.label}
                  </span>
                ))}
              </div>
              {donutData.length > 0 ? (
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie data={donutData} cx="50%" cy="50%" innerRadius={55} outerRadius={80} paddingAngle={3} dataKey="value"
                      label={({ value }) => value} labelLine={false}>
                      {donutData.map((entry, i) => <Cell key={entry.name || `cell-${i}`} fill={DONUT_COLORS[i % DONUT_COLORS.length]} />)}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-[200px] text-muted-foreground text-sm">No projects yet</div>
              )}
            </CardContent>
          </Card>

          {/* DMAIC Phase Progress */}
          <Card data-testid="dmaic-progress-panel">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <TrendingUp className="h-5 w-5" style={{ color: BRAND.cyan }} />
                DMAIC Progress
              </CardTitle>
              <CardDescription>Phase completion across all projects</CardDescription>
            </CardHeader>
            <CardContent className="pt-0">
              {phaseData.length > 0 ? (
                <div className="space-y-3 mt-2">
                  {phaseData.map((phase, i) => (
                    <div key={phase.name}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium">{phase.name}</span>
                        <span className="text-xs font-mono" style={{ color: PHASE_COLORS_ARR[i] }}>{phase.value}%</span>
                      </div>
                      <div className="h-3 bg-muted rounded-full overflow-hidden">
                        <div className="h-full rounded-full transition-all duration-700"
                          style={{ width: `${phase.value}%`, backgroundColor: PHASE_COLORS_ARR[i] }} />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex items-center justify-center h-[200px] text-muted-foreground text-sm">No DMAIC data</div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* ============ ROW 1.5: Workforce Planning Headcount ============ */}
        <WorkforceHeadcountWidget />

        {/* ============ ROW 1.55: Forecast Accuracy (planned vs actual) ============ */}
        <ForecastAccuracyWidget />

        {/* ============ ROW 1.57: Absenteeism — rolling sick-rate ============ */}
        <AbsenteeismWidget />

        {/* ============ ROW 1.6: Period briefing (any past ISO week) ============ */}
        <BriefingWidget />

        {/* ============ ROW 2: Quality Trend + KPI Performance + Workload ============ */}
        <div className="grid lg:grid-cols-3 gap-4">
          {/* Quality Trends */}
          <Card data-testid="quality-trend-panel">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <ClipboardCheck className="h-5 w-5" style={{ color: BRAND.cyan }} />
                  Quality Trends
                </CardTitle>
                <Button variant="ghost" size="sm" asChild>
                  <Link to="/audits" className="text-xs flex items-center gap-1">View <ArrowRight className="h-3 w-3" /></Link>
                </Button>
              </div>
              <CardDescription>6S audit scores over time</CardDescription>
            </CardHeader>
            <CardContent className="pt-0">
              {auditTrend.length > 0 ? (
                <ResponsiveContainer width="100%" height={200}>
                  <LineChart data={auditTrend}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                    <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} />
                    <Tooltip content={<CustomTooltip />} />
                    <Line type="monotone" dataKey="score" stroke={BRAND.cyan} strokeWidth={2.5} dot={{ r: 4, fill: BRAND.cyan }} name="Avg Score" />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-[200px] text-muted-foreground text-sm">No audit trend data</div>
              )}
              <div className="grid grid-cols-3 gap-2 mt-3 pt-3 border-t border-muted">
                <div className="text-center">
                  <p className="text-lg font-bold">{audits.total || 0}</p>
                  <p className="text-xs text-muted-foreground">Total</p>
                </div>
                <div className="text-center">
                  <p className="text-lg font-bold text-emerald-600">{audits.avg_score || 0}%</p>
                  <p className="text-xs text-muted-foreground">Avg Score</p>
                </div>
                <div className="text-center">
                  <p className="text-lg font-bold" style={{ color: BRAND.cyan }}>{audits.completed || 0}</p>
                  <p className="text-xs text-muted-foreground">Completed</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* KPI Performance */}
          <Card data-testid="kpi-performance-panel">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Target className="h-5 w-5" style={{ color: BRAND.cyan }} />
                  KPI Performance
                </CardTitle>
                <Button variant="ghost" size="sm" asChild>
                  <Link to="/kpis" className="text-xs flex items-center gap-1">View <ArrowRight className="h-3 w-3" /></Link>
                </Button>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              {kpiCatData.length > 0 ? (
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={kpiCatData} layout="vertical" margin={{ left: 0, right: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis type="number" tick={{ fontSize: 10 }} />
                    <YAxis dataKey="name" type="category" width={80} tick={{ fontSize: 11 }} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="Value" fill={BRAND.cyan} radius={[0, 3, 3, 0]} barSize={14} />
                    <Bar dataKey="Target" fill="#d1d5db" radius={[0, 3, 3, 0]} barSize={14} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-[200px] text-muted-foreground text-sm">No KPI data</div>
              )}
              <div className="grid grid-cols-3 gap-2 mt-3 pt-3 border-t border-muted">
                <div className="text-center">
                  <p className="text-lg font-bold text-emerald-600">{(kpis.on_target || 0) + (kpis.above_target || 0)}</p>
                  <p className="text-xs text-muted-foreground">On Target</p>
                </div>
                <div className="text-center">
                  <p className="text-lg font-bold text-rose-600">{kpis.below_target || 0}</p>
                  <p className="text-xs text-muted-foreground">Below</p>
                </div>
                <div className="text-center">
                  <p className="text-lg font-bold">{kpis.avg_performance || 0}%</p>
                  <p className="text-xs text-muted-foreground">Avg Perf</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Bewerker Workload */}
          <Card data-testid="workload-panel">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Users className="h-5 w-5" style={{ color: BRAND.cyan }} />
                Bewerker Workload
              </CardTitle>
              <CardDescription>
                {selectedWorkers.length > 0
                  ? `Comparing ${selectedWorkers.length} bewerker${selectedWorkers.length > 1 ? 's' : ''}`
                  : 'Top performers — select bewerkers to compare'}
              </CardDescription>
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                <Input
                  type="date" value={pickingFrom}
                  onChange={(e) => { setPickingFrom(e.target.value); loadDashboard(e.target.value, pickingTo); }}
                  className="h-8 text-xs w-[160px]"
                  data-testid="picking-period-from"
                />
                <span className="text-xs text-muted-foreground">to</span>
                <Input
                  type="date" value={pickingTo}
                  onChange={(e) => { setPickingTo(e.target.value); loadDashboard(pickingFrom, e.target.value); }}
                  className="h-8 text-xs w-[160px]"
                  data-testid="picking-period-to"
                />
                {(pickingFrom || pickingTo) && (
                  <button
                    onClick={() => { setPickingFrom(''); setPickingTo(''); loadDashboard('', ''); }}
                    className="text-xs text-rose-500 hover:text-rose-700 cursor-pointer"
                    data-testid="picking-period-clear"
                  >
                    Clear
                  </button>
                )}
              </div>
            </CardHeader>
            <CardContent className="pt-0 space-y-3">
              {workload.length > 0 && (
                <div className="flex items-center gap-2 flex-wrap" data-testid="workload-user-select">
                  <Popover open={bewerkerDropdownOpen} onOpenChange={setBewerkerDropdownOpen}>
                    <PopoverTrigger asChild>
                      <button
                        className="inline-flex items-center gap-2 h-9 px-3 rounded-md border border-border bg-background text-sm hover:bg-muted/50 transition-colors cursor-pointer min-w-[200px] justify-between"
                        data-testid="bewerker-dropdown-trigger"
                      >
                        <span className="truncate text-left">
                          {selectedWorkers.length === 0
                            ? 'Select bewerkers...'
                            : `${selectedWorkers.length} selected`}
                        </span>
                        <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[260px] p-0" align="start">
                      <Command>
                        <CommandInput placeholder="Search bewerker..." />
                        <CommandList>
                          <CommandEmpty>No bewerker found.</CommandEmpty>
                          <CommandGroup>
                            {workload.map((w) => {
                              const isSelected = selectedWorkers.includes(w.name);
                              return (
                                <CommandItem
                                  key={w.name}
                                  value={w.name}
                                  onSelect={() => {
                                    setSelectedWorkers(prev =>
                                      isSelected
                                        ? prev.filter(n => n !== w.name)
                                        : [...prev, w.name]
                                    );
                                  }}
                                  data-testid={`workload-user-${w.name.replace(/\s+/g, '-').toLowerCase()}`}
                                >
                                  <div className={`mr-2 flex h-4 w-4 items-center justify-center rounded-sm border ${isSelected ? 'border-primary bg-primary text-primary-foreground' : 'border-muted-foreground/40'}`}
                                    style={isSelected ? { backgroundColor: BRAND.cyan, borderColor: BRAND.cyan } : {}}
                                  >
                                    {isSelected && <Check className="h-3 w-3 text-white" />}
                                  </div>
                                  <span className="font-mono text-sm">{w.name}</span>
                                  <span className="ml-auto text-xs text-muted-foreground">{w.tasks} tasks</span>
                                </CommandItem>
                              );
                            })}
                          </CommandGroup>
                        </CommandList>
                        <div className="flex border-t p-1.5 gap-1">
                          <button
                            onClick={() => setSelectedWorkers(workload.map(w => w.name))}
                            className="flex-1 text-xs py-1.5 rounded hover:bg-muted transition-colors cursor-pointer font-medium"
                            style={{ color: BRAND.cyan }}
                            data-testid="workload-select-all"
                          >
                            Select All
                          </button>
                          <button
                            onClick={() => setSelectedWorkers([])}
                            className="flex-1 text-xs py-1.5 rounded text-rose-500 hover:bg-muted transition-colors cursor-pointer font-medium"
                            data-testid="workload-clear-selection"
                          >
                            Clear All
                          </button>
                        </div>
                      </Command>
                    </PopoverContent>
                  </Popover>
                  {selectedWorkers.length > 0 && (
                    <div className="flex items-center gap-1 flex-wrap">
                      {selectedWorkers.slice(0, 5).map(name => (
                        <span
                          key={name}
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium text-white"
                          style={{ backgroundColor: BRAND.cyan }}
                        >
                          {name}
                          <X
                            className="h-3 w-3 cursor-pointer hover:opacity-70"
                            onClick={() => setSelectedWorkers(prev => prev.filter(n => n !== name))}
                          />
                        </span>
                      ))}
                      {selectedWorkers.length > 5 && (
                        <span className="text-xs text-muted-foreground">+{selectedWorkers.length - 5} more</span>
                      )}
                    </div>
                  )}
                </div>
              )}
              {workloadData.length > 0 ? (
                <ResponsiveContainer width="100%" height={Math.max(200, workloadData.length * 40)}>
                  <BarChart data={workloadData} layout="vertical" margin={{ left: 0, right: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis type="number" tick={{ fontSize: 10 }} />
                    <YAxis dataKey="name" type="category" width={72} tick={{ fontSize: 11 }} />
                    <Tooltip
                      content={({ active, payload }) => {
                        if (!active || !payload?.length) return null;
                        const d = payload[0]?.payload;
                        return (
                          <div className="bg-popover border rounded-lg shadow-lg p-3 text-sm space-y-1">
                            <p className="font-semibold">{d.name}</p>
                            <p>Tasks: <span className="font-mono font-medium">{d.tasks}</span></p>
                            <p>Picks/Hr: <span className="font-mono font-medium">{d.avg_picks_per_hour}</span></p>
                            <p>Avg Pick Time: <span className="font-mono font-medium">{d.avg_pick_time} min</span></p>
                            <p>Hrs Active/Day: <span className="font-mono font-medium">{d.avg_hours_per_day}h</span></p>
                            <p>Days Active: <span className="font-mono font-medium">{d.days_active}</span></p>
                          </div>
                        );
                      }}
                    />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="tasks" fill={BRAND.cyan} name="Tasks" barSize={18} radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-[200px] text-muted-foreground text-sm">No picking data available</div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* ============ ROW 3: Module Overview + Goals Widget ============ */}
        <div className="grid lg:grid-cols-2 gap-4">
        <Card data-testid="module-overview">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <BarChart3 className="h-5 w-5" style={{ color: BRAND.cyan }} />
              Module Overview
            </CardTitle>
            <CardDescription>Status across all platform modules</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <ModuleProgressBar label="Projects" value={proj.completed || 0} max={proj.total || 1} icon={FolderKanban} color={BRAND.cyan} />
              <ModuleProgressBar label="6S Audits" value={audits.completed || 0} max={audits.total || 1} icon={ClipboardCheck} color={BRAND.emerald} />
              <ModuleProgressBar label="Checklists" value={checklists.completed || 0} max={checklists.total || 1} icon={CheckCircle2} color={BRAND.blue} />
              <ModuleProgressBar label="KPIs On Target" value={(kpis.on_target || 0) + (kpis.above_target || 0)} max={kpis.total || 1} icon={Target} color={BRAND.violet} />
              <ModuleProgressBar label="Damage Resolved" value={damage.resolved || 0} max={damage.total || 1} icon={ShieldAlert} color={BRAND.amber} />
            </div>
          </CardContent>
        </Card>

        <GoalsWidget token={localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token')} />
        </div>

        {/* ============ ROW 4: Operations Summary Cards (clickable shortcuts) ============ */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Link to="/picking" data-testid="stat-picking-link" className="block">
            <Card
              className="border-l-4 cursor-pointer transition-all duration-150 hover:-translate-y-0.5 hover:shadow-md active:translate-y-0 active:shadow-sm"
              style={{ borderLeftColor: BRAND.cyan }}
              data-testid="stat-picking"
            >
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <Package className="h-8 w-8" style={{ color: BRAND.cyan }} />
                  <div>
                    <p className="text-2xl font-bold">{picking.total_tasks || 0}</p>
                    <p className="text-xs text-muted-foreground">Picking Tasks</p>
                    {picking.avg_picks_hr > 0 && <p className="text-xs font-medium" style={{ color: BRAND.cyanDark }}>{picking.avg_picks_hr} picks/hr</p>}
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>
          <Link to="/expedition" data-testid="stat-expedition-link" className="block">
            <Card
              className="border-l-4 cursor-pointer transition-all duration-150 hover:-translate-y-0.5 hover:shadow-md active:translate-y-0 active:shadow-sm"
              style={{ borderLeftColor: BRAND.blue }}
              data-testid="stat-expedition"
            >
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <Truck className="h-8 w-8" style={{ color: BRAND.blue }} />
                  <div>
                    <p className="text-2xl font-bold">{expedition.total || 0}</p>
                    <p className="text-xs text-muted-foreground">Expeditions</p>
                    <p className="text-xs font-medium text-blue-600">{expedition.inbound || 0} in / {expedition.outbound || 0} out</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>
          <Link to="/damage-log" data-testid="stat-damage-link" className="block">
            <Card
              className="border-l-4 cursor-pointer transition-all duration-150 hover:-translate-y-0.5 hover:shadow-md active:translate-y-0 active:shadow-sm"
              style={{ borderLeftColor: BRAND.amber }}
              data-testid="stat-damage"
            >
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <ShieldAlert className="h-8 w-8" style={{ color: BRAND.amber }} />
                  <div>
                    <p className="text-2xl font-bold">{damage.total || 0}</p>
                    <p className="text-xs text-muted-foreground">Damage Reports</p>
                    {damage.open > 0 && <p className="text-xs font-medium text-rose-600">{damage.open} open</p>}
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>
          <Link to="/checklists" data-testid="stat-checklists-link" className="block">
            <Card
              className="border-l-4 cursor-pointer transition-all duration-150 hover:-translate-y-0.5 hover:shadow-md active:translate-y-0 active:shadow-sm"
              style={{ borderLeftColor: BRAND.emerald }}
              data-testid="stat-checklists"
            >
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="h-8 w-8" style={{ color: BRAND.emerald }} />
                  <div>
                    <p className="text-2xl font-bold">{checklists.total || 0}</p>
                    <p className="text-xs text-muted-foreground">Checklists</p>
                    <p className="text-xs font-medium text-emerald-600">{checklists.completed || 0} completed</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>
        </div>

        {/* ============ ROW 5: Recent Activity (Master Admin only, full width) ============ */}
        {isMasterAdmin && (
          <Card data-testid="recent-activity">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Clock className="h-5 w-5" style={{ color: BRAND.cyan }} />
                Recent Activity
              </CardTitle>
              <CardDescription>Activity from all team members</CardDescription>
            </CardHeader>
            <CardContent>
              {(data?.recent_activity || []).filter(a => a.user !== user?.name).length > 0 ? (
                <div className="grid md:grid-cols-2 gap-x-6 gap-y-1">
                  {data.recent_activity.filter(a => a.user !== user?.name).map((a) => (
                    <div key={a.id || `${a.user}-${a.action}-${a.time}`} className="flex items-start gap-3 py-2 border-b border-muted/50 last:border-b-0">
                      <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0" style={{ backgroundColor: BRAND.cyan }}>
                        {(a.user || '?')[0].toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm">
                          <span className="font-medium">{a.user}</span>
                          <span className="text-muted-foreground"> {a.action}d </span>
                          <span className="font-medium">{a.resource}</span>
                          {a.name && <span className="text-muted-foreground">: {a.name}</span>}
                        </p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {a.time ? new Date(a.time).toLocaleString() : ''}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-8">No recent activity from other team members</p>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
};

export default DashboardPage;
